<template>
  <v-card flat class="pa-0 clinical-form">
    <v-row v-if="readonly" no-gutters class="clinical-vitals">
      <v-col v-for="field in readonlyFields" :key="field.key" cols="6" sm="3" class="pa-1">
        <div class="clinical-vitals__item">
          <span>{{ field.label }}</span><strong>{{ field.value || '—' }}</strong>
        </div>
      </v-col>
      <v-col v-if="localModel.systems || localModel.notes" cols="12" class="pt-4">
        <p v-if="localModel.systems" class="clinical-note"><b>{{ _getLabel('SYSTEMS_FINDINGS') }}</b>{{ localModel.systems }}</p>
        <p v-if="localModel.notes" class="clinical-note"><b>{{ _getLabel('NOTES') }}</b>{{ localModel.notes }}</p>
      </v-col>
    </v-row>
    <v-row v-else no-gutters>
      <v-col cols="12">
        <v-text-field
          v-model="localModel.weight"
          outlined
          dense
          type="number"
          :label="_getLabel('WEIGHT')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.temperature"
          outlined
          dense
          type="number"
          :label="_getLabel('TEMPERATURE')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.heartRate"
          outlined
          dense
          type="number"
          :label="_getLabel('HEART_RATE')"
        ></v-text-field>
        <v-alert
          v-if="heartRateOutOfRange"
          dense
          outlined
          type="warning"
          class="mt-2"
        >
          {{ outOfRangeText(heartRateRange) }}
        </v-alert>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.respiratoryRate"
          outlined
          dense
          type="number"
          :label="_getLabel('RESPIRATORY_RATE')"
        ></v-text-field>
        <v-alert
          v-if="respiratoryRateOutOfRange"
          dense
          outlined
          type="warning"
          class="mt-2"
        >
          {{ outOfRangeText(respiratoryRateRange) }}
        </v-alert>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.mucous"
          outlined
          dense
          :label="_getLabel('MUCOUS')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.crt"
          outlined
          dense
          :label="_getLabel('CRT')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.hydration"
          outlined
          dense
          :label="_getLabel('HYDRATION')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.lymphNodes"
          outlined
          dense
          :label="_getLabel('LYMPH_NODES')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.bodyScore"
          outlined
          dense
          type="number"
          :label="_getLabel('BODY_SCORE')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="localModel.consciousness"
          outlined
          dense
          :label="_getLabel('CONSCIOUSNESS')"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="localModel.systems"
          outlined
          dense
          :label="_getLabel('SYSTEMS_FINDINGS')"
          rows="2"
        ></v-textarea>
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="localModel.notes"
          outlined
          dense
          :label="_getLabel('NOTES')"
          rows="2"
        ></v-textarea>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: "PhysicalExamForm",
  props: {
    value: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    species: {
      type: Number,
      default: null,
      required: false,
    },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      localModel: { ...this.value },
    };
  },
  computed: {
    readonlyFields() {
      return [
        ['weight', 'WEIGHT'], ['temperature', 'TEMPERATURE'], ['heartRate', 'HEART_RATE'], ['respiratoryRate', 'RESPIRATORY_RATE'],
        ['mucous', 'MUCOUS'], ['crt', 'CRT'], ['hydration', 'HYDRATION'], ['lymphNodes', 'LYMPH_NODES'], ['bodyScore', 'BODY_SCORE'], ['consciousness', 'CONSCIOUSNESS'],
      ].map(([key, label]) => ({ key, label: this._getLabel(label), value: this.localModel[key] }));
    },
    heartRateRange() {
      if (this.species === 1) return { min: 60, max: 160 };
      if (this.species === 2) return { min: 120, max: 240 };
      return null;
    },
    respiratoryRateRange() {
      if (this.species === 1) return { min: 10, max: 30 };
      if (this.species === 2) return { min: 20, max: 30 };
      return null;
    },
    heartRateOutOfRange() {
      return this.isOutOfRange(this.parseNumber(this.localModel.heartRate), this.heartRateRange);
    },
    respiratoryRateOutOfRange() {
      return this.isOutOfRange(this.parseNumber(this.localModel.respiratoryRate), this.respiratoryRateRange);
    },
  },
  methods: {
    parseNumber(value) {
      if (value === null || value === undefined || value === "") return null;
      const parsed = parseFloat(value.toString().replace(",", "."));
      return Number.isNaN(parsed) ? null : parsed;
    },
    isOutOfRange(value, range) {
      if (value === null || !range) return false;
      return value < range.min || value > range.max;
    },
    outOfRangeText(range) {
      if (!range) return this._getLabel('OUT_OF_RANGE');
      return `${this._getLabel('OUT_OF_RANGE')} (${range.min}-${range.max}).`;
    },
  },
  watch: {
    value: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.localModel)) {
          this.localModel = { ...(val || {}) };
        }
      },
      deep: true,
    },
    localModel: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.value)) {
          this.$emit("input", val);
        }
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.clinical-vitals { margin: -4px; }
.clinical-vitals__item { background:#f5f6fa; border-radius:8px; min-height:64px; padding:10px; display:flex; flex-direction:column; gap:4px; }
.clinical-vitals__item span { color:#667085; font-size:11px; font-weight:700; }
.clinical-vitals__item strong { color:#193153; font-size:15px; }
.clinical-note { white-space:pre-wrap; color:#193153; margin:0 0 12px; }
.clinical-note b { display:block; font-size:12px; color:#667085; margin-bottom:4px; }
</style>
